import { useCallback, useState } from 'react';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { resolveSourceFileOpenUrl } from 'services/storage-service';
import { Source } from '../types/chat';

/** Appends a PDF page fragment (`#page=n`) so the browser viewer deep-links. */
const withPage = (url: string, page?: number): string =>
  page && page > 0 ? `${url}#page=${page}` : url;

/** Returns the value when it is an http(s) URL, else undefined. */
const asHttpUrl = (value?: string): string | undefined => {
  const v = (value || '').trim();
  return /^https?:\/\//i.test(v) ? v : undefined;
};

/**
 * The web URL to open for a link-type source. Such sources have no stored file,
 * so Storage returns `forbidden`/`url:null`. The URL lives in `uri` (set by the
 * backend), or — for pills persisted before that — in the source/chunk file name.
 */
const linkUrlOf = (source: Pick<Source, 'uri' | 'fileName' | 'chunks'>): string | undefined =>
  asHttpUrl(source.uri) ||
  asHttpUrl(source.fileName) ||
  asHttpUrl(source.chunks?.find((c) => asHttpUrl(c.fileName))?.fileName);

/**
 * Opens a KB source file in a new tab, optionally at a specific page.
 * Shared by the source pill and inline citation markers.
 */
export const useOpenSourceFile = () => {
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const openSourceFile = useCallback(
    async (source: Pick<Source, 'fileId' | 'uri' | 'fileName' | 'chunks'>, page?: number) => {
      if (loading) return;
      setLoading(true);
      try {
        // Link-type sources have no stored file — open their URL directly instead
        // of resolving a Storage file (which returns forbidden for a link).
        const linkUrl = linkUrlOf(source);
        if (linkUrl) {
          window.open(withPage(linkUrl, page), '_blank', 'noopener,noreferrer');
          return;
        }
        const { openUrl } = await resolveSourceFileOpenUrl(
          source.fileId,
          configurationSettings,
          undefined,
        );
        if (!openUrl) {
          toast({
            variant: 'destructive',
            title: t('ERROR') || 'Error',
            description:
              t('FAILED_TO_OPEN_FILE') ||
              'Unable to open this file. It may no longer be available.',
          });
          return;
        }
        window.open(withPage(openUrl, page), '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Failed to open file:', error);
        toast({
          variant: 'destructive',
          title: t('ERROR') || 'Error',
          description:
            t('FAILED_TO_OPEN_FILE') ||
            'Unable to open this file. It may no longer be available.',
        });
      } finally {
        setLoading(false);
      }
    },
    [loading, configurationSettings, toast, t],
  );

  return { openSourceFile, loading };
};
